import { Skeleton } from "@/components/ui/skeleton";
import { useClientLinks } from "@/features/clients/hooks/use-scoped-client";
import { useSearchSessions } from "@/features/sessions/hooks/use-sessions";
import type { SearchSessionsOptions } from "@/features/sessions/types";
import { useDateFormat, useTranslation } from "@/features/utils/i18n";
import { cn } from "@/lib/utils";
import {
  ChannelIcon,
  formatDuration,
  ResultIcon,
} from "@/views/backoffice/calls/components/conversation-meta";
import { CaretRightIcon } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

/**
 * The conversations something was built from — a qualification submission, a
 * Messagerie request — one click from each, newest first. Each module keeps its
 * own list of session ids; this only knows the ids, and reads them through the
 * sessions search (`ids=`), so the staff read-only masking applies as anywhere.
 *
 * `fallback` is the search to run when there are no ids, for rows written before
 * their module recorded any (e.g. the person's conversations carrying a tag).
 */
export function LinkedSessions({
  sessionIds,
  fallback,
  title,
  highlightId,
  className,
}: {
  sessionIds: string[];
  fallback?: Omit<SearchSessionsOptions, "ids">;
  title?: string;
  /** The conversation the page is about, marked in the list. */
  highlightId?: string;
  className?: string;
}) {
  const { t } = useTranslation("common");
  const { format } = useDateFormat();
  const links = useClientLinks();
  const query: SearchSessionsOptions | null = sessionIds.length
    ? { ids: sessionIds.slice(-100).join(","), limit: 100 }
    : fallback
      ? { limit: 20, ...fallback }
      : null;
  const { data, isLoading } = useSearchSessions(
    { sort_by: "created_at", sort_order: "desc", ...query },
    // Nothing to look for: the search without filters would be every conversation
    { enabled: !!query },
  );
  // Sorted here too: the server's order is a request, and the demo data has none
  const sessions = (query ? (data?.data ?? []) : [])
    .slice()
    .sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );

  return (
    <section className={cn("space-y-2", className)}>
      <h3 className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
        {title ?? t("linked_sessions.title")}
      </h3>
      {query && isLoading ? (
        <Skeleton className="h-12 w-full" />
      ) : sessions.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t("linked_sessions.empty")}
        </p>
      ) : (
        <ul className="divide-y rounded-lg border">
          {sessions.map((session) => (
            <li key={session.id}>
              <Link
                to={links.call(session.id)}
                className={cn(
                  "flex items-center gap-3 px-4 py-2.5 hover:bg-muted/50",
                  session.id === highlightId && "bg-muted/40",
                )}
              >
                <ChannelIcon session={session} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">
                    {session.name || t("linked_sessions.untitled")}
                  </div>
                  {session.summary && (
                    <div className="truncate text-xs text-muted-foreground">
                      {session.summary}
                    </div>
                  )}
                </div>
                <div className="shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                  <div>{format(session.created_at, "PPp")}</div>
                  {session.duration > 0 && (
                    <div>{formatDuration(session.duration / 1000)}</div>
                  )}
                </div>
                <ResultIcon session={session} />
                <CaretRightIcon className="size-4 shrink-0 text-muted-foreground" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
